export interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  originalPrice?: number;
  category: string;
  subcategory: string;
  images: string[];
  colors: string[];
  materials: string[];
  description: string;
  specifications: {
    fabric: string;
    work: string;
    occasion: string;
    washCare: string;
    length?: string;
    width?: string;
    weight?: string;
  };
  inStock: boolean;
  isNew?: boolean;
  isFeatured?: boolean;
  rating: number;
  reviewCount: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  image: string;
  description: string;
  subcategories: { name: string; slug: string }[];
}

export const categories: Category[] = [
  {
    id: "1",
    name: "Sarees",
    slug: "sarees",
    image: "/images/heritage/collection-silks.jpg",
    description: "Timeless elegance woven in every thread",
    subcategories: [
      { name: "Banarasi Silk", slug: "banarasi-silk" },
      { name: "Kanjivaram", slug: "kanjivaram" },
      { name: "Chanderi", slug: "chanderi" },
      { name: "Tussar Silk", slug: "tussar-silk" },
      { name: "Cotton Sarees", slug: "cotton-sarees" },
      { name: "Designer Sarees", slug: "designer-sarees" },
    ],
  },
  {
    id: "2",
    name: "Lehengas",
    slug: "lehengas",
    image: "/images/heritage/collection-bridal.jpg",
    description: "Bridal dreams crafted with perfection",
    subcategories: [
      { name: "Bridal Lehengas", slug: "bridal-lehengas" },
      { name: "Party Wear", slug: "party-wear" },
      { name: "Reception", slug: "reception" },
      { name: "Sangeet", slug: "sangeet" },
    ],
  },
  {
    id: "3",
    name: "Fabrics",
    slug: "fabrics",
    image: "https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=800&fit=crop",
    description: "Premium fabrics for your creative vision",
    subcategories: [
      { name: "Pure Silk", slug: "pure-silk" },
      { name: "Brocade", slug: "brocade" },
      { name: "Velvet", slug: "velvet" },
      { name: "Georgette", slug: "georgette" },
      { name: "Organza", slug: "organza" },
    ],
  },
  {
    id: "4",
    name: "Suits & Sets",
    slug: "suits-sets",
    image: "/images/heritage/collection-suit.jpg",
    description: "Graceful ensembles for every occasion",
    subcategories: [
      { name: "Anarkali Suits", slug: "anarkali-suits" },
      { name: "Palazzo Sets", slug: "palazzo-sets" },
      { name: "Straight Suits", slug: "straight-suits" },
      { name: "Sharara Sets", slug: "sharara-sets" },
    ],
  },
  {
    id: "5",
    name: "Dupattas",
    slug: "dupattas",
    image: "https://images.unsplash.com/photo-1617627143233-46b5c19c7d3f?w=600&h=800&fit=crop",
    description: "The finishing touch of elegance",
    subcategories: [
      { name: "Banarasi Dupatta", slug: "banarasi-dupatta" },
      { name: "Phulkari", slug: "phulkari" },
      { name: "Bandhani", slug: "bandhani" },
      { name: "Silk Dupatta", slug: "silk-dupatta" },
    ],
  },
  {
    id: "6",
    name: "Accessories",
    slug: "accessories",
    image: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=600&h=800&fit=crop",
    description: "Curated pieces to complete your look",
    subcategories: [
      { name: "Jewelry", slug: "jewelry" },
      { name: "Clutches", slug: "clutches" },
      { name: "Hair Accessories", slug: "hair-accessories" },
    ],
  },
];

export const products: Product[] = [
  {
    id: "1",
    name: "Royal Banarasi Silk Saree",
    slug: "royal-banarasi-silk-saree",
    price: 45000,
    originalPrice: 52000,
    category: "sarees",
    subcategory: "banarasi-silk",
    images: [
      "/images/products/saree-banarasi.jpg",
    ],
    colors: ["Maroon", "Gold"],
    materials: ["Pure Silk"],
    description: "An exquisite Banarasi silk saree featuring intricate zari work that tells stories of royal heritage. Each thread is carefully woven by master artisans in Varanasi, creating a masterpiece that embodies centuries of tradition.",
    specifications: {
      fabric: "Pure Banarasi Silk",
      work: "Handwoven Zari, Traditional Motifs",
      occasion: "Wedding, Reception, Festive",
      washCare: "Dry Clean Only",
      length: "6.3 meters (with blouse piece)",
      weight: "750 grams",
    },
    inStock: true,
    isNew: true,
    isFeatured: true,
    rating: 4.9,
    reviewCount: 127,
  },
  {
    id: "2",
    name: "Kanjivaram Temple Border Saree",
    slug: "kanjivaram-temple-border-saree",
    price: 68000,
    category: "sarees",
    subcategory: "kanjivaram",
    images: [
      "/images/products/saree-kanjivaram.jpg",
    ],
    colors: ["Deep Green", "Gold"],
    materials: ["Mulberry Silk"],
    description: "A stunning Kanjivaram saree with the iconic temple border pattern. Woven in the traditional style of Kanchipuram, this saree features pure mulberry silk and genuine gold zari.",
    specifications: {
      fabric: "Pure Mulberry Silk",
      work: "Temple Border, Peacock Motifs",
      occasion: "Wedding, Traditional Ceremonies",
      washCare: "Dry Clean Only",
      length: "6.2 meters (with blouse piece)",
      weight: "850 grams",
    },
    inStock: true,
    isFeatured: true,
    rating: 4.8,
    reviewCount: 89,
  },
  {
    id: "3",
    name: "Bridal Lehenga - Crimson Dreams",
    slug: "bridal-lehenga-crimson-dreams",
    price: 185000,
    originalPrice: 210000,
    category: "lehengas",
    subcategory: "bridal-lehengas",
    images: [
      "/images/products/lehenga-bridal.jpg",
    ],
    colors: ["Crimson Red", "Antique Gold"],
    materials: ["Raw Silk", "Net"],
    description: "A breathtaking bridal lehenga that combines traditional craftsmanship with contemporary elegance. Features hand-embroidered floral motifs, kundan work, and a stunning flared silhouette.",
    specifications: {
      fabric: "Raw Silk with Net Dupatta",
      work: "Hand Embroidery, Kundan, Zardozi",
      occasion: "Wedding",
      washCare: "Professional Dry Clean Only",
      weight: "4.5 kg (complete set)",
    },
    inStock: true,
    isNew: true,
    isFeatured: true,
    rating: 5.0,
    reviewCount: 45,
  },
  {
    id: "4",
    name: "Chanderi Silk Saree - Pastel Rose",
    slug: "chanderi-silk-saree-pastel-rose",
    price: 18500,
    category: "sarees",
    subcategory: "chanderi",
    images: [
      "/images/products/saree-chanderi.jpg",
    ],
    colors: ["Pastel Pink", "Silver"],
    materials: ["Chanderi Silk"],
    description: "A delicate Chanderi silk saree in a soft pastel rose shade. Known for its sheer texture and lightweight feel, perfect for day events and casual celebrations.",
    specifications: {
      fabric: "Pure Chanderi Silk",
      work: "Traditional Chanderi Weave",
      occasion: "Day Events, Pooja, Casual",
      washCare: "Gentle Hand Wash / Dry Clean",
      length: "6.3 meters (with blouse piece)",
      weight: "450 grams",
    },
    inStock: true,
    rating: 4.7,
    reviewCount: 156,
  },
  {
    id: "5",
    name: "Designer Anarkali - Midnight Bloom",
    slug: "designer-anarkali-midnight-bloom",
    price: 32000,
    category: "suits-sets",
    subcategory: "anarkali-suits",
    images: [
      "/images/products/suit-anarkali.jpg",
    ],
    colors: ["Midnight Blue", "Rose Gold"],
    materials: ["Georgette", "Net"],
    description: "A stunning floor-length Anarkali in midnight blue with intricate rose gold embroidery. Features a flowing silhouette with a heavily worked bodice.",
    specifications: {
      fabric: "Georgette with Net Dupatta",
      work: "Thread Embroidery, Sequins",
      occasion: "Festive, Party, Reception",
      washCare: "Dry Clean Only",
    },
    inStock: true,
    isNew: true,
    rating: 4.6,
    reviewCount: 78,
  },
  {
    id: "6",
    name: "Pure Brocade Fabric - Imperial Gold",
    slug: "pure-brocade-fabric-imperial-gold",
    price: 4500,
    category: "fabrics",
    subcategory: "brocade",
    images: [
      "/images/products/fabric-brocade.jpg",
    ],
    colors: ["Gold", "Ivory"],
    materials: ["Brocade"],
    description: "Premium Banarasi brocade fabric with intricate gold thread work. Perfect for creating custom blouses, lehengas, or traditional garments.",
    specifications: {
      fabric: "Pure Brocade",
      work: "Zari Weaving",
      occasion: "Custom Tailoring",
      washCare: "Dry Clean Only",
      width: "44 inches",
    },
    inStock: true,
    rating: 4.8,
    reviewCount: 234,
  },
  {
    id: "7",
    name: "Phulkari Dupatta - Sunset Hues",
    slug: "phulkari-dupatta-sunset-hues",
    price: 8500,
    originalPrice: 9500,
    category: "dupattas",
    subcategory: "phulkari",
    images: [
      "/images/products/dupatta-phulkari.jpg",
    ],
    colors: ["Orange", "Pink", "Yellow"],
    materials: ["Cotton", "Silk Thread"],
    description: "A vibrant Phulkari dupatta featuring the traditional embroidery of Punjab. Each piece is hand-embroidered, making it unique and special.",
    specifications: {
      fabric: "Cotton Base with Silk Thread",
      work: "Traditional Phulkari Embroidery",
      occasion: "Festive, Casual, Sangeet",
      washCare: "Gentle Hand Wash",
      length: "2.5 meters",
      width: "1 meter",
    },
    inStock: true,
    rating: 4.5,
    reviewCount: 167,
  },
  {
    id: "8",
    name: "Tussar Silk Saree - Earthy Elegance",
    slug: "tussar-silk-saree-earthy-elegance",
    price: 28000,
    category: "sarees",
    subcategory: "tussar-silk",
    images: [
      "/images/products/saree-tussar.jpg",
    ],
    colors: ["Beige", "Rust"],
    materials: ["Tussar Silk"],
    description: "A beautiful Tussar silk saree with natural earthy tones and traditional hand-painted Madhubani motifs. The raw texture of Tussar silk adds a unique charm.",
    specifications: {
      fabric: "Pure Tussar Silk",
      work: "Hand-painted Madhubani Art",
      occasion: "Art Events, Cultural, Festive",
      washCare: "Dry Clean Only",
      length: "6.3 meters (with blouse piece)",
      weight: "550 grams",
    },
    inStock: true,
    isFeatured: true,
    rating: 4.9,
    reviewCount: 92,
  },
  {
    id: "9",
    name: "Party Lehenga - Rose Quartz",
    slug: "party-lehenga-rose-quartz",
    price: 65000,
    category: "lehengas",
    subcategory: "party-wear",
    images: [
      "/images/products/lehenga-party.jpg",
    ],
    colors: ["Blush Pink", "Silver"],
    materials: ["Organza", "Net"],
    description: "A dreamy party wear lehenga in soft rose quartz shade. Features delicate sequin work and a flowing organza dupatta with pearl detailing.",
    specifications: {
      fabric: "Organza with Net Dupatta",
      work: "Sequin Embroidery, Pearl Work",
      occasion: "Sangeet, Party, Reception",
      washCare: "Dry Clean Only",
      weight: "2.8 kg (complete set)",
    },
    inStock: true,
    isNew: true,
    rating: 4.7,
    reviewCount: 56,
  },
  {
    id: "10",
    name: "Heritage Kundan Necklace Set",
    slug: "heritage-kundan-necklace-set",
    price: 125000,
    category: "accessories",
    subcategory: "jewelry",
    images: [
      "/images/products/accessory-kundan.jpg",
    ],
    colors: ["Gold", "Emerald"],
    materials: ["Gold Plated", "Kundan", "Pearls"],
    description: "An opulent bridal jewelry set featuring traditional Kundan work with emerald accents and baroque pearls. Includes necklace, earrings, and maang tikka.",
    specifications: {
      fabric: "Gold Plated Brass",
      work: "Kundan Setting, Pearl Drops",
      occasion: "Wedding, Bridal",
      washCare: "Store in jewelry box, avoid moisture",
    },
    inStock: true,
    isFeatured: true,
    rating: 4.9,
    reviewCount: 34,
  },
  {
    id: "11",
    name: "Sharara Set - Emerald Symphony",
    slug: "sharara-set-emerald-symphony",
    price: 42000,
    category: "suits-sets",
    subcategory: "sharara-sets",
    images: [
      "/images/products/suit-sharara.jpg",
    ],
    colors: ["Emerald Green", "Gold"],
    materials: ["Velvet", "Georgette"],
    description: "A luxurious sharara set in rich emerald green velvet with gold gota patti work. The flared sharara pants create a stunning silhouette.",
    specifications: {
      fabric: "Velvet with Georgette Dupatta",
      work: "Gota Patti, Thread Work",
      occasion: "Festive, Party, Mehendi",
      washCare: "Dry Clean Only",
    },
    inStock: true,
    rating: 4.6,
    reviewCount: 89,
  },
  {
    id: "12",
    name: "Designer Cotton Saree - Ocean Breeze",
    slug: "designer-cotton-saree-ocean-breeze",
    price: 6500,
    category: "sarees",
    subcategory: "cotton-sarees",
    images: [
      "https://images.unsplash.com/photo-1617627143233-46b5c19c7d3f?w=800&h=1000&fit=crop",
      "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800&h=1000&fit=crop",
    ],
    colors: ["Teal", "White"],
    materials: ["Pure Cotton"],
    description: "A comfortable and stylish cotton saree perfect for everyday elegance. Features a modern block print design in soothing ocean hues.",
    specifications: {
      fabric: "Pure Handloom Cotton",
      work: "Block Print",
      occasion: "Daily Wear, Office, Casual",
      washCare: "Machine Wash / Hand Wash",
      length: "6.3 meters (with blouse piece)",
      weight: "380 grams",
    },
    inStock: true,
    rating: 4.4,
    reviewCount: 312,
  },
];

export const heroSlides = [
  {
    id: 1,
    title: "Timeless Elegance",
    subtitle: "Bridal Couture '24",
    description: "Exquisite handcrafted pieces celebrating Indian heritage",
    image: "/images/heritage/hero-bridal.jpg",
    cta: "Explore Collection",
    link: "/shop?category=lehengas",
  },
  {
    id: 2,
    title: "Woven Heritage",
    subtitle: "Banarasi & Kanjivaram",
    description: "Tradition meets timeless beauty in every weave",
    image: "/images/heritage/hero-charcoal.jpg",
    cta: "Shop Sarees",
    link: "/shop?category=sarees",
  },
  {
    id: 3,
    title: "Festive Splendor",
    subtitle: "Designer Collection",
    description: "Celebrate every moment with grace and grandeur",
    image: "/images/heritage/hero-modern.jpg",
    cta: "View All",
    link: "/shop",
  },
];

export const featuredCollections = [
  {
    id: 1,
    title: "Bridal Edit",
    image: "/images/heritage/collection-bridal.jpg",
    link: "/shop?category=lehengas&subcategory=bridal-lehengas",
  },
  {
    id: 2,
    title: "Silk Sarees",
    image: "/images/heritage/collection-silks.jpg",
    link: "/shop?category=sarees",
  },
  {
    id: 3,
    title: "Festive Suits",
    image: "/images/heritage/collection-suit.jpg",
    link: "/shop?category=suits-sets",
  },
  {
    id: 4,
    title: "Artisan Fabrics",
    image: "/images/heritage/manifesto-weaving.jpg",
    link: "/shop?category=fabrics",
  },
];

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}

export function getProductsByCategory(category: string): Product[] {
  return products.filter((p) => p.category === category);
}

export function getFeaturedProducts(): Product[] {
  return products.filter((p) => p.isFeatured);
}

export function getNewArrivals(): Product[] {
  return products.filter((p) => p.isNew);
}

export function formatPrice(price: number): string {
  // Check for currency preference (default to INR)
  // Ensure we are in browser environment
  const currency = typeof window !== 'undefined' ? (localStorage.getItem('leestone_currency') || 'INR') : 'INR';
  
  if (currency === 'USD') {
    // Approx exchange rate: 1 USD = 84 INR
    // Using a fixed rate for simplicity in this demo.
    // In production, this should come from a context or API.
    const usdPrice = price / 84;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(usdPrice);
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(price);
}